import { fetchEntities } from '@/services/master/entitiy/entityService';
import { Entity } from '@/types/entityType';
import { Avatar } from 'flowbite-react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { FaArrowAltCircleDown, FaBars, FaSignOutAlt } from 'react-icons/fa';
import { FaCaretDown } from 'react-icons/fa6';
import defaultAvatar from '../../assets/images/default-user.jpg';
import logoHiGuard from '../../assets/images/HI-GUARD 2.png';
import { logout } from '../../utils/auth';
import {
	getEntity,
	getUser,
	removeEntity,
	removeToken,
	removeUser,
} from '../../utils/handleToken';
import LanguageButton from './LanguageButton';

export default function Navbar({
	toggleSidebar,
	changeLanguage,
}: {
	changeLanguage: (language: string) => void;
	toggleSidebar: () => void;
}) {
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [userName, setUserName] = useState('');
	const router = useRouter();
	const pathname = usePathname();
	const [hasError, setHasError] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const [isSidebarDisabled, setIsSidebarDisabled] = useState(false);

	const [entityData, setEntityData] = useState<Entity | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		// Cek apakah sidebar harus dinonaktifkan
		const checkSidebarDisabled = () => {
			setIsSidebarDisabled(
				document.body.classList.contains('sidebar-disabled')
			);
		};

		// Jalankan pengecekan saat komponen dimount
		checkSidebarDisabled();

		// Buat observer untuk memantau perubahan class pada body
		const observer = new MutationObserver(checkSidebarDisabled);
		observer.observe(document.body, {
			attributes: true,
			attributeFilter: ['class'],
		});

		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		// Hanya jalankan di client-side
		if (typeof window !== 'undefined') {
			const loadEntityData = async () => {
				try {
					const userEntities = getEntity();
					if (!userEntities?.length) {
						console.log('[Navbar] No user entities found');
						setLoading(false);
						return;
					}

					const response = await fetchEntities();
					if (response?.success && response.data?.length > 0) {
						const userEntity = response.data.find(
							(entity) => entity.me_entity_id === userEntities[0]
						);
						if (userEntity) {
							setEntityData(userEntity);
							setHasError(false);
						}
					}
				} catch (err: any) {
					console.error('[Navbar] Error loading entity:', err);
					setError(err.message || 'Gagal memuat data entity');
				} finally {
					setLoading(false);
				}
			};

			loadEntityData();
		}
	}, []);

	useEffect(() => {
		const name = getUser();
		if (name) {
			setUserName(name);
		}
	}, []);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsDropdownOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const handleLogout = () => {
		removeEntity();
		removeToken();
		removeUser();
		window.location.reload();
		router.push('/login');
	};

	const renderLogo = () => {
		// Jika masih dalam proses build atau SSR
		if (typeof window === 'undefined') {
			return (
				<Image
					src={logoHiGuard}
					alt="HI-GUARD Logo"
					width={200}
					height={100}
					priority
				/>
			);
		}

		if (loading) {
			return (
				<div className="h-12 w-48 bg-gray-200 animate-pulse rounded"></div>
			);
		}

		if (!entityData?.me_logo_med || hasError) {
			console.log('[Navbar] Using default logo');
			return (
				<Image
					src={logoHiGuard}
					alt="HI-GUARD Logo"
					width={200}
					height={100}
					priority
				/>
			);
		}
		console.log('[Navbar] Using entity logo:', entityData.me_logo_med);
		return (
			<Image
				src={entityData.me_logo_med}
				alt={entityData.me_entity_name || 'Entity Logo'}
				width={200}
				height={100}
				priority
				unoptimized
				style={{ objectFit: 'contain' }}
				onError={() => {
					setHasError(true);
				}}
			/>
		);
	};

	const handleSidebarToggle = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (!isSidebarDisabled) {
			toggleSidebar();
		}
	};

	const handleAvatarClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (!isSidebarDisabled) {
			setIsDropdownOpen(!isDropdownOpen);
		}
	};

	return (
		<nav className="fixed top-0 w-full z-50 bg-white shadow rounded-b-3xl border-b-4 border-blue-500">
			<div className="px-5 py-2 flex items-center justify-between">
				{/* Bagian kiri: Icon hamburger */}
				<div className="flex items-center mx-24">
					<FaBars
						onClick={handleSidebarToggle} 
						className={`cursor-pointer text-xl ${
							isSidebarDisabled ? 'text-gray-400' : 'text-dark'
						}`}
					/>
				</div>

				{/* Bagian tengah: Logo Entity */}
				<div className="hidden md:flex flex-grow justify-center mr-16">
					{renderLogo()}
				</div>

				{/* Bagian kanan: Grup Language button, avatar, dan logout */}
				<div className="flex items-center space-x-4">
					{/* <div className="hidden md:block">
						<LanguageButton changeLanguage={changeLanguage} />
					</div> */}

					<div className="flex items-center">
						<span className="text-dark font-semibold text-sm">{userName}</span>
					</div>

					<div className="relative" ref={dropdownRef}>
						<button
							onClick={handleAvatarClick}
							className={`flex items-center focus:outline-none hover:bg-gray-100 hover:rounded-full ${
								isSidebarDisabled ? 'opacity-50 cursor-not-allowed' : ''
							}`}
						>
							<Avatar
								alt="User settings"
								img={defaultAvatar.src}
								rounded
								className={`cursor-pointer size-10 ${
									isSidebarDisabled ? 'cursor-not-allowed' : ''
								}`}
							/>
							<div className="flex justify-end text-dark">
								<FaCaretDown
									className={`w-4 h-4 ml-2 transition-transform duration-500 ${
										isDropdownOpen ? 'rotate-180 duration-500' : ''
									} ${isSidebarDisabled ? 'opacity-50' : ''}`}
								/>
							</div>
						</button>
						{isDropdownOpen && !isSidebarDisabled && (
							<div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10">
								<Link
									href="/hra-dasbor-ent"
									className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded-md"
								>
									Dashboard
								</Link>
								<Link
									href="/auth/change-password"
									className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded-md"
								>
									Ganti Password
								</Link>
								<div className="border-t border-gray-100"></div>
								<button
									onClick={handleLogout}
									className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded-md"
								>
									Logout
								</button>
							</div>
						)}
					</div>
				</div>
			</div>
		</nav>
	);
}
